import { DataSchema, type Data } from "./schema";
import raw from "./factors.json";

/**
 * Loads and validates the factor dataset.
 *
 * The JSON is checked against the zod schema at module load. If `factors.json`
 * has a problem (missing field, wrong type, a category id that doesn't exist),
 * we throw a clear error naming exactly what's wrong — instead of failing in a
 * confusing way deep inside a component.
 */
function load(): Data {
  const parsed = DataSchema.safeParse(raw);

  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((i) => `  • ${i.path.join(".") || "(root)"}: ${i.message}`)
      .join("\n");
    throw new Error(
      `factors.json is invalid. Fix these and reload:\n${issues}`,
    );
  }

  const data = parsed.data;

  // Cross-reference check: every factor.category must point at a real category.
  const categoryIds = new Set(data.categories.map((c) => c.id));
  const orphan = data.factors.find((f) => !categoryIds.has(f.category));
  if (orphan) {
    throw new Error(
      `factors.json: factor "${orphan.id}" has category "${orphan.category}", ` +
        `which is not defined in "categories".`,
    );
  }

  // Duplicate id check: ids must be unique (they key persisted state).
  const seen = new Set<string>();
  for (const f of data.factors) {
    if (seen.has(f.id)) {
      throw new Error(`factors.json: duplicate factor id "${f.id}".`);
    }
    seen.add(f.id);
  }

  return data;
}

export const data: Data = load();
